import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';

import { Col, Row, Button } from 'twyne-new.ui';

import { Card, Property } from '@/components/reports/card';
import ObjectModal from '@Components/modals/ObjectModal';
import { formatDateTime } from '@Utils/index';

import store from '../../../stores/inquiry'

import CustomFieldsResult from './CustomFieldsResult'
import IntegrationResult from './IntegrationResult'
import PathSessionResult from './PathSessionResult'
import RewardResults from './RewardResults';
import UserSessionResult from './UserSessionResult'
import { formatUtcDate, fallback, formatBool, formatData, CardTitle } from './utils'

export default function Results({ data }) {
  return <div className='flex flex-col gap-4 mt-4'>
    {data.map((userData, idx) => <UserResult key={idx} data={userData} />)}
  </div>
}

Results.propTypes = {
  data: PropTypes.array,
}

const ButtonModal = ({ handleToggle }) => <Col size='2' className='my-3'>
  <Button variant='light' color='accent' onClick={handleToggle}>
    more details
  </Button>
</Col>

const UserResultComponent = ({ data, userData }) => <div>
  <Row>
    <Property label="E-mail" value={data?.email} />
    <Property label="Address 1" value={data?.address1} />
    <Property label="Address 2" value={data?.address2} />
    <Property label="City" value={data?.city} />
    <Property label="State" value={data?.state} />
    <Property label="ZIP" value={data?.zip} />
    <Property label="Country" value={data?.country} />
    <Property label="Phone" value={data?.phone} />
    <Property label="Date of birth" value={formatUtcDate(data?.dob)} />
    <Property label="Gender" value={data?.gender} />
    <Property label="Unsubscribed" value={formatBool(data?.unsubscribed)} />
    <Property label="Site" value={fallback(store.getSiteName(data?.siteID))} />
    <Property label="Validated" value={formatBool(data?.validated)} />
    <Property label="Opt-out" value={formatBool(data?.isOptout)} />
    <Property label="Updated At" value={formatDateTime(data?.updated_at)} />
    <Property label="Revenue" value={<NumberFormat
      value={data?.revenue?.toFixed(2) || 0}
      displayType="text"
      thousandSeparator=","
      decimalSeparator="."
      prefix={'$'}
      decimalScale={2}
      fixedDecimalScale={true}
    />} />
  </Row>
  <ObjectModal data={userData} button={ButtonModal} title="User Fields" />
</div>

function UserResult({ data: [key, { data, integrations, rewards }] }) {
  const [customFieldsTotal, setCustomFieldsTotal] = useState(0)

  const userIntegrations = useMemo(() => integrations.map(({ _source }) => _source), [integrations])

  const { userData, embed } = useMemo(() => formatData(data, userIntegrations), [data])

  return <>
    <Card title={<>
      <CardTitle
        title={`${data?.first_name} ${data?.last_name || ''}`}
        tipTitle={`ID: ${data?.id}`}
        subtitle={fallback(store.getSiteName(data?.siteID))}
        tipSubtitle={`Site ID: ${data?.siteID}`}
      />
    </>}>
      <UserResultComponent data={data} userData={userData} />
      {Object.entries(data?.customFieldsParsed).length ? <>
        <Card defaultOpen={false} title={<CardTitle title="CustomFields" subtitle={`total: ${customFieldsTotal || 0}`} />}>
          <CustomFieldsResult data={data?.customFieldsParsed} siteId={data?.siteID} answered={setCustomFieldsTotal} />
        </Card>
      </> : null}
      {data?.userSessions.length ? <>
        <Card defaultOpen={!data?.userSessions.length == 0} title={<CardTitle title="Registrations" subtitle={`total: ${data?.userSessions.length || 0}`} />}>
          <UserSessionResult data={data?.userSessions} />
        </Card>
      </> : null}
      {embed.pathSessions.length || embed.integrations.length ? <>
        <Card defaultOpen={embed.pathSessions.length || embed.integrations.length} title={<CardTitle title="Embed" />}>
          <PathSessionResult data={embed.pathSessions} />
          <IntegrationResult data={embed.integrations} />
        </Card>
      </> : null}
      {rewards.length ? <RewardResults data={rewards} /> : null}
    </Card>
  </>
}

UserResult.propTypes = {
  data: PropTypes.array,
}
